// -- copyright
// OpenProject is an open source project management software.
// Copyright (C) 2012-2023 the OpenProject GmbH
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License version 3.
//
// OpenProject is a fork of ChiliProject, which is a fork of Redmine. The copyright follows:
// Copyright (C) 2006-2013 Jean-Philippe Lang
// Copyright (C) 2010-2013 the ChiliProject Team
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License
// as published by the Free Software Foundation; either version 2
// of the License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program; if not, write to the Free Software
// Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
//
// See COPYRIGHT and LICENSE files for more details.
//++

import {
  Component, Input, EventEmitter, Output,
} from '@angular/core';
import { WorkPackageResource } from 'core-app/features/hal/resources/work-package-resource';
import { WorkPackageRelationsHierarchyService } from 'core-app/features/work-packages/components/wp-relations/wp-relations-hierarchy/wp-relations-hierarchy.service';
import { I18nService } from 'core-app/core/i18n/i18n.service';
import { WorkPackageNotificationService } from 'core-app/features/work-packages/services/notifications/work-package-notification.service';

@Component({
  templateUrl: './wp-breadcrumb-parent.html',
  selector: 'wp-breadcrumb-parent',
})
export class WorkPackageBreadcrumbParentComponent {
  @Input('workPackage') workPackage:WorkPackageResource;

  @Output('onSwitch') onSwitch = new EventEmitter<boolean>();

  public isSaving = false;

  public text = {
    edit_parent: this.I18n.t('js.relation_buttons.change_parent'),
    set_or_remove_parent: this.I18n.t('js.relations_autocomplete.parent_placeholder'),
    remove_parent: this.I18n.t('js.relation_buttons.remove_parent'),
    set_parent: this.I18n.t('js.relation_buttons.set_parent'),
  };

  private editing:boolean;

  public constructor(
    protected readonly I18n:I18nService,
    protected readonly wpRelationsHierarchy:WorkPackageRelationsHierarchyService,
    protected readonly notificationService:WorkPackageNotificationService,
  ) {
  }

  public canModifyParent():boolean {
    return !!this.workPackage.changeParent;
  }

  public get parent() {
    return this.workPackage && this.workPackage.parent;
  }

  public get active():boolean {
    return this.editing;
  }

  public close():void {
    this.toggle(false);
  }

  public open():void {
    this.toggle(true);
  }

  public updateParent(newParent:WorkPackageResource|null) {
    this.close();
    const newParentId = newParent ? newParent.id : null;
    if (_.get(this.parent, 'id', null) === newParentId) {
      return;
    }

    this.isSaving = true;
    this.wpRelationsHierarchy.changeParent(this.workPackage, newParentId)
      .catch((error:any) => {
        this.notificationService.handleRawError(error, this.workPackage);
      })
      .then(() => this.isSaving = false); // Behaves as .finally()
  }

  /**
   * 解析 ISO 8601 格式的持续时间字符串为小时数
   * @param duration ISO 8601 格式的持续时间字符串（如 PT2H30M）
   * @returns 小时数（如 2.5）
   */
  private parseDuration(duration: string): number {
    if (!duration) return 0;
    
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const match = duration.match(regex);
    
    if (!match) return 0;
    
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);
    
    return hours + minutes / 60 + seconds / 3600;
  }

  // ========== 新增的复制方法 ==========
  /**
   * 复制工作包的标题和链接到剪贴板
   * 格式: 项目名称 任务标题 任务链接
   */
  public copyWorkPackageInfo():void {
    console.log('=== copyWorkPackageInfo 被调用 ===');
    
    // 检查工作包数据是否存在
    if (!this.workPackage) {
      console.error('工作包数据为空');
      return;
    }
    
    // 获取项目名称
    let projectName = '';
    if (this.workPackage.project && this.workPackage.project.name) {
      projectName = this.workPackage.project.name;
    } else if (this.workPackage._links && this.workPackage._links.project && this.workPackage._links.project.title) {
      projectName = this.workPackage._links.project.title;
    } else {
      projectName = '当前项目';
    }
    
    // 获取任务标题
    const taskTitle = this.workPackage.subject || this.workPackage.name || '';

    // 获取处理人名称（处理人：表（work_packages），字段（responsible_id：关联users表的id））
    const assignee = this.workPackage.responsible?.name || '';

    // 获取自评时长并解析为小时数
    const durationString = this.workPackage.estimatedTime || '';
    const evaluationTime = this.parseDuration(durationString);

    // 获取预估时长
    const estimatedTime = this.workPackage.customField105 || '';

    // 获取任务链接
    const taskLink = `${window.location.origin}/wp/${this.workPackage.id}`;
    
    // 检查状态是否需要包含自评时长
    const statusId = this.workPackage.status?.id;
    console.log('statusId:======', statusId, typeof statusId);
    const numericStatusId = typeof statusId === 'string' ? parseInt(statusId, 10) : statusId;
    const shouldIncludeEvaluationTime = [13, 15, 17].includes(numericStatusId);
    console.log('shouldIncludeEvaluationTime:======', shouldIncludeEvaluationTime);
    
    // 构建要复制的内容
    let content = `${projectName}-【${taskTitle}-${assignee}】 ${taskLink}【${estimatedTime}】`;
    console.log('content:======', content);
    if (shouldIncludeEvaluationTime) {
      content = `【${evaluationTime}】${content}`;
    }
    content = content.trim();
    
    console.log('要复制的内容:', content);
    
    // 复制到剪贴板
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(content)
        .then(() => {
          console.log('✅ 复制成功');
          this.showCopySuccess();
        })
        .catch((err) => {
          console.error('剪贴板API复制失败:', err);
          this.fallbackCopy(content);
        });
    } else {
      console.log('使用降级方案复制');
      this.fallbackCopy(content);
    }
  }
  
  /**
   * 显示复制成功提示
   */
  private showCopySuccess():void {
    // 显示复制成功的弹窗提示
    this.showToast('复制成功！');
  }
  
  /**
   * 显示弹窗提示
   */
  private showToast(message: string):void {
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      font-weight: bold;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      animation: slideInRight 0.3s ease-out;
    `;
    
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s';
      setTimeout(() => {
        toast.remove();
        style.remove();
      }, 300);
    }, 2000);
  }
  
  /**
   * 降级复制方案（兼容旧浏览器）
   */
  private fallbackCopy(text: string):void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      
      if (success) {
        console.log('✅ 降级方案复制成功');
        this.showCopySuccess();
      } else {
        console.error('降级方案复制失败');
        alert('复制失败，请手动复制');
      }
    } catch (err) {
      document.body.removeChild(textarea);
      console.error('降级方案执行错误:', err);
      alert('复制失败，请手动复制');
    }
  }
  // ========== 复制方法结束 ==========

  private toggle(state:boolean) {
    if (this.editing !== state) {
      this.editing = state;
      this.onSwitch.emit(this.editing);
    }
  }
}